import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  VERSION,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { IconService } from './@shared/services/icon.service';
import { AuthService } from './auth/services/auth.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { I18nService } from './i18n';
import { ParkbaseStorageService } from './@shared/services/parkbaseStorage.service';
import { NbThemeService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Title } from '@angular/platform-browser';
import { NbAccessChecker } from '@nebular/security';
import { LogService } from './@core';
import { default as appConfig } from 'src/assets/app.config.json';
import { CookieService } from 'ngx-cookie-service';

// eslint-disable-next-line @typescript-eslint/ban-types
declare const gtag: Function;

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterContentInit, OnInit, OnDestroy {
  title = 'ParkbaseGUI9';
  appVersion: string;

  constructor(
    public accessChecker: NbAccessChecker,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private changeDetectionRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private authService: AuthService,
    private iconService: IconService,
    private parkbaseStorageService: ParkbaseStorageService,
    private themeService: NbThemeService,
    private logger: LogService,
    private cookieService: CookieService,
    private oidcSecurityService: OidcSecurityService
  ) {
    // set version
    this.appVersion = environment.version;

    // set title
    this.title = 'ParkbaseGui 9 Beta preview - ' + this.appVersion + ' - Angular v' + VERSION.full;

    // configure logging
    this.logger.config(appConfig.logging);
  }

  ngOnInit() {
    // Setup Eva and custom icons
    this.iconService.registerIcons();

    // Load default theme or theme selection stored by user
    const selectedTheme = this.parkbaseStorageService.getTheme();
    this.themeService.changeTheme(selectedTheme);

    // Setup translations
    const lang = this.cookieService.get('lang');
    if (lang) {
      this.i18nService.init(lang, environment.supportedLanguages);
    } else {
      this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
    }

    // GTag
    this.addGAScript();

    // test error logging
    // this.localError();

    // Setup auth with angular-auth-oidc-client
    this.oidcSecurityService
      .checkAuthMultiple()
      .subscribe(([{ isAuthenticated, userData, accessToken }]) => {
        if (userData && isAuthenticated) {
          this.authService.setCredentials();
        }
        if (!isAuthenticated) {
          this.router.navigateByUrl('/account').then(() => {});
        }
      });
  }

  ngAfterContentInit(): void {
    this.changeDetectionRef.detectChanges();
    this.logger.info('GUI started!');
  }

  ngOnDestroy(): void {
    this.authService.clearCredentials();
  }

  private addGAScript() {
    const gtagScript: HTMLScriptElement = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + environment.trackingId;
    document.head.prepend(gtagScript);
    /** Disable automatic page view hit to fix duplicate page view count  **/
    gtag('config', environment.trackingId, { send_page_view: false });
  }

  private localError(): void {
    throw Error('The app component has thrown an error!');
    this.logger.error('The app component has thrown an error!');
  }
}
